import { useHandler } from "@redotech/react-util/hook";
import { parseGid } from "@redotech/redo-model/shopify/gid-utils";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ExtensionShopifyClient } from "@redotech/shopify-client/ajax";
import debounce from "lodash/debounce";
import { createContext, ReactNode, useEffect, useState } from "react";
import { StorefrontClient } from "../client/storefront";
import { log } from "../monitoring/log";
import { reportTiming } from "../monitoring/timing";
import { shopifyProductHandle } from "../shopify";
import {
  getProductFormElements,
  isAddToCartButtonEnabled,
  isValidProductForm,
} from "./pdp-utils";
import { getPreviewModeAndStoreInLocalStorage } from "./preview-mode";

enum TryFirstState {
  INITIALIZING = "INITIALIZING",
  INELIGIBLE = "INELIGIBLE",
  READY = "READY",
}

type TryFirstBase = {
  autocheck: WidgetAutocheck;
  extensionShopifyClient: ExtensionShopifyClient;
};

type TryFirstNotLoadedContext = TryFirstBase & {
  state: Exclude<TryFirstState, TryFirstState.READY>;
};

type TryFirstLoadedContext = TryFirstBase & {
  state: TryFirstState.READY;
  productForm: HTMLElement;
  sellingPlanId: string;
};

export type TryFirstContextType = TryFirstBase &
  (TryFirstLoadedContext | TryFirstNotLoadedContext);

export const TryFirstContext = createContext<TryFirstContextType | undefined>(
  undefined,
);

interface TryFirstProviderProps {
  autocheck: WidgetAutocheck;
  extensionShopifyClient: StorefrontClient;
  container: HTMLElement;
  children: ReactNode;
}

const DEBOUNCE_CHANGES_MS = 100; // If things are changing more frequently than 100ms, keep waiting to see if it settles down
const MAX_DEBOUNCE_MS = 1000; // But don't wait longer than 1 second to check

export function TryFirstProvider({
  autocheck,
  extensionShopifyClient,
  container,
  children,
}: TryFirstProviderProps) {
  const [contextValue, setContextValue] = useState<TryFirstContextType>({
    autocheck,
    extensionShopifyClient,
    state: TryFirstState.INITIALIZING,
  });
  const checkEligibilitySignal = useShouldCheckEligibility();
  const isPreviewMode = getPreviewModeAndStoreInLocalStorage();

  useEffect(() => {
    const checkTryFirstEligibility = async () => {
      if (!autocheck.tryFirst?.widget?.enabled && !isPreviewMode) {
        setContextValue({
          autocheck,
          extensionShopifyClient,
          state: TryFirstState.INELIGIBLE,
        });
        return;
      }

      const formElements = getProductFormElements(container);
      if (!formElements) {
        setContextValue({
          autocheck,
          extensionShopifyClient,
          state: TryFirstState.INELIGIBLE,
        });
        return;
      }

      const { productForm, addToCartButton } = formElements;

      if (!isAddToCartButtonEnabled(addToCartButton)) {
        setContextValue({
          autocheck,
          extensionShopifyClient,
          state: TryFirstState.INELIGIBLE,
        });
        return;
      }

      const productHandle =
        container.parentElement?.dataset.productHandle ||
        shopifyProductHandle(window.location.href);
      if (!productHandle) {
        setContextValue({
          autocheck,
          extensionShopifyClient,
          state: TryFirstState.INELIGIBLE,
        });
        return;
      }

      try {
        let productSellingPlans: { id: string; name: string }[] = [];

        // TODO: [performance optimization] If app embed is installed, grab the selling plans from the app embed JSON (create a product selling plan cache)
        const plans =
          await extensionShopifyClient.getProductSellingPlans(productHandle);
        if (plans) {
          productSellingPlans = plans;
        }
        reportTiming("tryFirst.sellingPlansRetrieved");

        log("Product selling plans:", {
          productHandle,
          sellingPlans: productSellingPlans,
          tryFirstSellingPlanId: autocheck.tryFirst?.sellingPlanId,
        });

        const tryFirstSellingPlanId = autocheck.tryFirst?.sellingPlanId;
        const foundMatchingSellingPlan =
          !!tryFirstSellingPlanId &&
          productSellingPlans.find((plan) => {
            const planId = parseGid(plan.id);
            return planId === tryFirstSellingPlanId;
          });

        if (!foundMatchingSellingPlan) {
          setContextValue({
            autocheck,
            extensionShopifyClient,
            state: TryFirstState.INELIGIBLE,
          });
          return;
        }

        setContextValue({
          autocheck,
          extensionShopifyClient,
          state: TryFirstState.READY,
          productForm,
          sellingPlanId: tryFirstSellingPlanId,
        });
        reportTiming("tryFirst.buttonIsEligibleToShow");
      } catch (error) {
        console.error("Error fetching product selling plans:", error);
        setContextValue({
          autocheck,
          extensionShopifyClient,
          state: TryFirstState.INELIGIBLE,
        });
      }
      reportTiming("tryFirst.eligibilityChecked");
    };

    void checkTryFirstEligibility();
  }, [
    autocheck,
    extensionShopifyClient,
    container,
    checkEligibilitySignal,
    isPreviewMode,
  ]);

  return (
    <TryFirstContext.Provider value={contextValue}>
      {children}
    </TryFirstContext.Provider>
  );
}

const useShouldCheckEligibility = () => {
  const [checkEligibilitySignal, setCheckEligibilitySignal] =
    useState<symbol>(Symbol());
  const markEligibilityStale = useHandler(
    debounce(
      () => {
        setCheckEligibilitySignal(Symbol());
      },
      DEBOUNCE_CHANGES_MS,
      { maxWait: MAX_DEBOUNCE_MS, leading: false, trailing: true },
    ),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      markEligibilityStale();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "value"],
    });

    const handleFormChange = (event: Event) => {
      const target = event.target as HTMLElement;
      if (isValidProductForm(target)) {
        markEligibilityStale();
      }
    };

    document.addEventListener("change", handleFormChange);
    document.addEventListener("input", handleFormChange);

    return () => {
      observer.disconnect();
      document.removeEventListener("change", handleFormChange);
      document.removeEventListener("input", handleFormChange);
    };
  }, [markEligibilityStale]);

  return checkEligibilitySignal;
};
